/**
 * Revenue Report Page
 *
 * Enhanced revenue reporting with filters, charts, and Excel export.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Space,
  Typography,
  Select,
  DatePicker,
  Row,
  Col,
  Statistic,
  Button,
  Switch,
  Progress,
  message,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  DownloadOutlined,
  ReloadOutlined,
  DollarOutlined,
  RiseOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { colors, spacing } from '../components/design-system/tokens';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

interface Commission {
  id: string;
  contractRef: string;
  supplier: string;
  buyer: string;
  commodity: string;
  commissionAmount: number;
  commissionRate: number;
  totalValue: number;
  status: string;
  contractDate: string;
}

interface TrendData {
  month: string;
  revenue: number;
  commission: number;
  profit: number;
  contracts: number;
}

interface Company {
  id: string;
  name: string;
  shortName?: string;
  type: string;
}

interface Commodity {
  id: string;
  name: string;
}

const currentYear = new Date().getFullYear();
const cropYears = Array.from({ length: 5 }, (_, i) => currentYear - i);

const RevenueReport: React.FC = () => {
  const navigate = useNavigate();
  const [cropYear, setCropYear] = useState<number | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [supplierId, setSupplierId] = useState<string | undefined>();
  const [buyerId, setBuyerId] = useState<string | undefined>();
  const [commodityId, setCommodityId] = useState<string | undefined>();
  const [hideEstimated, setHideEstimated] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [period, setPeriod] = useState<string>('6months');

  const { data: companiesData } = useQuery({
    queryKey: ['companies', 'all'],
    queryFn: async () => {
      const response = await api.get('/companies', { params: { limit: 500 } });
      return (response as any)?.data?.companies || (response as any)?.companies || [];
    },
  });

  const { data: commoditiesData } = useQuery({
    queryKey: ['commodities'],
    queryFn: async () => {
      const response = await api.get('/commodities', { params: { limit: 200 } });
      return (response as any)?.data?.commodities || (response as any)?.commodities || [];
    },
  });

  const companies: Company[] = companiesData || [];
  const commodities: Commodity[] = commoditiesData || [];
  const suppliers = companies.filter((c) => c.type === 'SUPPLIER');
  const buyers = companies.filter((c) => c.type === 'BUYER');

  const { data: commissionsData, isLoading: commissionsLoading, refetch: refetchCommissions } = useQuery({
    queryKey: ['revenue-commissions', cropYear, dateRange, supplierId, buyerId, period],
    queryFn: async () => {
      const params: any = { status: 'ALL', limit: 500, period };
      if (supplierId) params.supplierId = supplierId;
      if (buyerId) params.buyerId = buyerId;
      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }
      const response = await api.get('/financial/commissions', { params });
      const data = (response as any)?.data || response;
      return data as { commissions: Commission[]; pagination: any };
    },
  });

  const { data: trendData, isLoading: trendLoading, refetch: refetchTrend } = useQuery({
    queryKey: ['revenue-trend', period],
    queryFn: async () => {
      const response = await api.get('/financial/revenue-trend', { params: { period } });
      const data = (response as any)?.data || response;
      return (data?.data || []) as TrendData[];
    },
  });

  let commissions = commissionsData?.commissions || [];
  const trends = trendData || [];

  if (cropYear) {
    commissions = commissions.filter((c) => {
      const year = new Date(c.contractDate).getFullYear();
      return year === cropYear;
    });
  }

  if (commodityId) {
    commissions = commissions.filter((c) => 
      c.commodity?.toLowerCase().includes(commodities.find((cm) => cm.id === commodityId)?.name?.toLowerCase() || '')
    );
  }

  if (hideEstimated) {
    commissions = commissions.filter((c) => c.status === 'PAID');
  }

  const totals = useMemo(() => {
    return commissions.reduce(
      (acc, c) => {
        acc.revenue += c.totalValue;
        acc.commission += c.commissionAmount;
        return acc;
      },
      { revenue: 0, commission: 0 }
    );
  }, [commissions]);

  const margin = totals.revenue > 0 ? (totals.commission / totals.revenue) * 100 : 0;

  const topBuyers = useMemo(() => {
    const buyerTotals: Record<string, number> = {};
    commissions.forEach((c) => {
      buyerTotals[c.buyer] = (buyerTotals[c.buyer] || 0) + c.commissionAmount;
    });
    return Object.entries(buyerTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [commissions]);

  const topSuppliers = useMemo(() => {
    const supplierTotals: Record<string, number> = {};
    commissions.forEach((c) => {
      supplierTotals[c.supplier] = (supplierTotals[c.supplier] || 0) + c.commissionAmount;
    });
    return Object.entries(supplierTotals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [commissions]);

  const maxTrendRevenue = Math.max(...trends.map((t) => t.revenue), 1);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('atlas_token') || localStorage.getItem('auth_token');
      if (!token) {
        message.error('Authentication required');
        return;
      }

      const exportFilters: Record<string, unknown> = {};
      if (supplierId) exportFilters.supplierId = supplierId;
      if (buyerId) exportFilters.buyerId = buyerId;
      if (dateRange) {
        exportFilters.dateFrom = dateRange[0].format('YYYY-MM-DD');
        exportFilters.dateTo = dateRange[1].format('YYYY-MM-DD');
      }

      const response = await fetch('/api/v1/export/excel', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportType: 'revenue-report',
          filters: exportFilters,
        }),
      });

      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const filename = response.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
        || `revenue-report-${dayjs().format('YYYY-MM-DD')}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Export downloaded successfully');
    } catch (error) {
      message.error('Failed to export report');
    } finally {
      setExporting(false);
    }
  }, [supplierId, buyerId, dateRange]);

  const handleRefresh = () => {
    refetchCommissions();
    refetchTrend();
  };

  const handleRowClick = (record: Commission) => {
    navigate(`/contracts/${record.contractRef}`);
  };

  const columns: ColumnsType<Commission> = [
    {
      title: 'Contract',
      dataIndex: 'contractRef',
      key: 'contractRef',
      width: 110,
      render: (ref: string) => <Text strong>{ref}</Text>,
      sorter: (a, b) => a.contractRef.localeCompare(b.contractRef),
    },
    {
      title: 'Date',
      dataIndex: 'contractDate',
      key: 'contractDate',
      width: 100,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.contractDate).unix() - dayjs(b.contractDate).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Buyer',
      dataIndex: 'buyer',
      key: 'buyer',
      width: 140,
      ellipsis: true,
    },
    {
      title: 'Commodity',
      dataIndex: 'commodity',
      key: 'commodity',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Total Value',
      dataIndex: 'totalValue',
      key: 'totalValue',
      width: 110,
      align: 'right',
      render: (val: number) => `$${Number(val).toLocaleString('en-US', { minimumFractionDigits: 0 })}`,
      sorter: (a, b) => a.totalValue - b.totalValue,
    },
    {
      title: 'Rate',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      width: 70,
      align: 'right',
      render: (rate: number) => `${rate}%`,
    },
    {
      title: 'Commission',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      width: 110,
      align: 'right',
      render: (amt: number) => (
        <Text strong style={{ color: colors.status.positive }}>
          ${Number(amt).toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </Text>
      ),
      sorter: (a, b) => a.commissionAmount - b.commissionAmount,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      render: (status: string) => (
        <Text type={status === 'PAID' ? 'success' : status === 'OVERDUE' ? 'danger' : 'secondary'}>
          {status}
        </Text>
      ),
    },
  ];

  const maxTopValue = Math.max(...topBuyers.map((b) => b[1]), ...topSuppliers.map((s) => s[1]), 1);

  return (
    <div style={{ padding: spacing.xl }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <Title level={2} style={{ margin: 0 }}>Revenue Report</Title>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={handleExport} loading={exporting}>
              Export Excel
            </Button>
          </Space>
        </div>

        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Revenue"
                value={totals.revenue}
                precision={0}
                prefix={<DollarOutlined />}
                valueStyle={{ color: colors.primary[60] }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Commission"
                value={totals.commission}
                precision={2}
                prefix="$"
                valueStyle={{ color: colors.status.positive }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Avg Margin"
                value={margin}
                precision={2}
                suffix="%"
                prefix={<RiseOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Contracts"
                value={commissions.length}
                prefix={<BarChartOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <Card title="Revenue Trend" size="small">
              {trends.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {trends.map((t) => (
                    <div key={t.month} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Text style={{ width: 80, fontSize: 12 }}>{t.month}</Text>
                      <div style={{ flex: 1 }}>
                        <Tooltip title={`$${t.revenue.toLocaleString()}`}>
                          <Progress
                            percent={Math.round((t.revenue / maxTrendRevenue) * 100)}
                            showInfo={false}
                            strokeColor={colors.primary[60]}
                            size="small"
                          />
                        </Tooltip>
                      </div>
                      <Text style={{ width: 80, textAlign: 'right', fontSize: 12 }}>
                        ${(t.revenue / 1000).toFixed(0)}k
                      </Text>
                    </div>
                  ))}
                </div>
              ) : (
                <Text type="secondary">No trend data available</Text>
              )}
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Row gutter={16}>
              <Col span={12}>
                <Card title="Top Buyers" size="small">
                  {topBuyers.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {topBuyers.map(([name, value]) => (
                        <div key={name}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <Text ellipsis style={{ maxWidth: 100, fontSize: 12 }}>{name}</Text>
                            <Text style={{ fontSize: 12 }}>${(value / 1000).toFixed(1)}k</Text>
                          </div>
                          <Progress
                            percent={Math.round((value / maxTopValue) * 100)}
                            showInfo={false}
                            strokeColor={colors.status.positive}
                            size="small"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">No data</Text>
                  )}
                </Card>
              </Col>
              <Col span={12}>
                <Card title="Top Suppliers" size="small">
                  {topSuppliers.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {topSuppliers.map(([name, value]) => (
                        <div key={name}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <Text ellipsis style={{ maxWidth: 100, fontSize: 12 }}>{name}</Text>
                            <Text style={{ fontSize: 12 }}>${(value / 1000).toFixed(1)}k</Text>
                          </div>
                          <Progress
                            percent={Math.round((value / maxTopValue) * 100)}
                            showInfo={false}
                            strokeColor={colors.primary[60]}
                            size="small"
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Text type="secondary">No data</Text>
                  )}
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <Card>
          <Space style={{ marginBottom: 16 }} wrap>
            <Select
              value={cropYear}
              onChange={setCropYear}
              style={{ width: 120 }}
              placeholder="Crop Year"
              allowClear
              options={cropYears.map((y) => ({ value: y, label: `${y}` }))}
            />
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
              format="YYYY-MM-DD"
            />
            <Select
              value={period}
              onChange={setPeriod}
              style={{ width: 130 }}
              options={[
                { value: '1month', label: 'Last Month' },
                { value: '3months', label: 'Last 3 Months' },
                { value: '6months', label: 'Last 6 Months' },
                { value: '1year', label: 'Last Year' },
              ]}
            />
            <Select
              value={supplierId}
              onChange={setSupplierId}
              style={{ width: 160 }}
              placeholder="Supplier"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={suppliers.map((s) => ({ value: s.id, label: s.shortName || s.name }))}
            />
            <Select
              value={buyerId}
              onChange={setBuyerId}
              style={{ width: 160 }}
              placeholder="Buyer"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={buyers.map((b) => ({ value: b.id, label: b.shortName || b.name }))}
            />
            <Select
              value={commodityId}
              onChange={setCommodityId}
              style={{ width: 140 }}
              placeholder="Commodity"
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={commodities.map((c) => ({ value: c.id, label: c.name }))}
            />
            <Space>
              <Text>Hide Estimated:</Text>
              <Switch checked={hideEstimated} onChange={setHideEstimated} size="small" />
            </Space>
            {(cropYear || dateRange || supplierId || buyerId || commodityId || hideEstimated) && (
              <Button
                onClick={() => {
                  setCropYear(undefined);
                  setDateRange(null);
                  setSupplierId(undefined);
                  setBuyerId(undefined);
                  setCommodityId(undefined);
                  setHideEstimated(false);
                }}
              >
                Clear Filters
              </Button>
            )}
          </Space>

          <Table
            columns={columns}
            dataSource={commissions}
            rowKey="id"
            loading={commissionsLoading || trendLoading}
            pagination={{
              pageSize: 25,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total) => `${total} contracts`,
            }}
            size="middle"
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' },
            })}
            scroll={{ x: 1000 }}
          />
        </Card>
      </Space>
    </div>
  );
};

export default RevenueReport;
